import { DateTime } from "luxon";
import { Nullable, Optional } from "sonobello.utilities.react";

import { DisqualifyingReason, DisqualifyingReasonsType } from "../../types/DisqualifyingReason";
import { ICalendarService } from "../../Calendar/types/ICalendar";
import AllMedicalQuestionsEvaluator from "../../steps/Medical/compositions/AllMedicalQuestionsEvaluator";
import IMedicalConclusions from "../../steps/Medical/types/IMedicalConclusions";
import IMedicalQualification from "../../steps/Medical/types/IMedicalQualification";
import { IPatient, evaluatePatientDisqualification, getBmi } from "../../types/Entities/Patient";
import { ReservationCenter } from "../../types/IReservation";
import INoteTag from "./INoteTag";

const serviceSubCategoryName: INoteTag<{
  reservation: Nullable<{ service: Pick<ICalendarService, "serviceSubCategoryName"> }>;
}> = {
  buildTagContent: ({ reservation }) => reservation?.service.serviceSubCategoryName || null
};

const obxTag: INoteTag<unknown> = { buildTagContent: () => "OBX" };

const businessUnitTag: INoteTag<{
  reservation: Nullable<{ service: Pick<ICalendarService, "businessUnit"> }>;
}> = {
  buildTagContent: ({ reservation }) => reservation?.service.businessUnit.name || null
};

const patientHeightWeightBmiTag: INoteTag<{ patient: Pick<IPatient, "height" | "weight"> }> = {
  buildTagContent: ({ patient }) => {
    const tokens: string[] = [];
    if (patient.height) tokens.push(`${patient.height.feet}ft${patient.height.inches}in`);
    if (patient.weight) tokens.push(`${patient.weight}lbs`);
    if (patient.height && patient.weight) tokens.push(`${getBmi(patient.height, patient.weight)} BMI`);
    return tokens.join(", ") || null;
  }
};

const patientDateOfBirthNoteTag: INoteTag<{ patient: Pick<IPatient, "dateOfBirth"> }> = {
  buildTagContent: ({ patient }) =>
    patient.dateOfBirth ? DateTime.fromISO(patient.dateOfBirth).toLocaleString(DateTime.DATE_MED) : null
};

const patientMedicalQualificationPassFailNoteTag: INoteTag<{
  patient: Pick<IPatient, "height" | "weight" | "dateOfBirth">;
  medicalForm: IMedicalQualification;
}> = {
  buildTagContent: ({ patient, medicalForm }) => {
    if (!medicalForm) return null;
    let disqualifyingReason: Optional<DisqualifyingReasonsType> = undefined;
    if (patient.height && patient.weight && patient.dateOfBirth)
      disqualifyingReason = evaluatePatientDisqualification(
        patient as Parameters<typeof evaluatePatientDisqualification>[0]
      );

    if (disqualifyingReason || AllMedicalQuestionsEvaluator.isNonCandidate(medicalForm)) return "MQ Fail";

    if (!disqualifyingReason && !AllMedicalQuestionsEvaluator.isComplete(medicalForm)) return null;

    return "MQ Pass";
  }
};

const patientMedicalQualificationTimestampedNoteTag: INoteTag<{ patient: Pick<IPatient, "qualificationDateUtc"> }> = {
  buildTagContent: ({ patient }) =>
    `MQ Pass ${
      patient.qualificationDateUtc
        ? DateTime.fromISO(patient.qualificationDateUtc, { zone: "UTC" }).toLocaleString(DateTime.DATE_MED)
        : DateTime.now().toLocaleString(DateTime.DATE_MED)
    }`
};

const patientDistanceToCenterNoteTag: INoteTag<{ patient: Pick<IPatient, "distanceToCenter"> }> = {
  buildTagContent: ({ patient }) => {
    const distance = patient.distanceToCenter;
    return distance !== undefined ? `${distance.toFixed(2)} mi` : distance || null;
  }
};

const patientDistanceToReservationCenterNoteTag: INoteTag<{
  reservation: { center: Pick<ReservationCenter, "distance"> };
}> = {
  buildTagContent: ({
    reservation: {
      center: { distance }
    }
  }) => {
    return distance !== null ? `${distance.toFixed(2)} mi` : distance || null;
  }
};

const centerNameNoteTag: INoteTag<{ center: Pick<{ name: string }, "name"> }> = {
  buildTagContent: ({ center }) => center.name
};

const disqualifyingReasonsNoteTag: INoteTag<{
  disqualifyingReasons: DisqualifyingReason[];
  patient: Pick<IPatient, "height" | "weight" | "dateOfBirth">;
}> = {
  buildTagContent: ({ disqualifyingReasons, patient }) => {
    {
      if (!patient.height || !patient.weight || !patient.dateOfBirth) return null;
      const patientDisqualifyingReason = evaluatePatientDisqualification(
        patient as Parameters<typeof evaluatePatientDisqualification>[0]
      );
      if (!patientDisqualifyingReason || !disqualifyingReasons) return null;
      const disqualifyingReason = DisqualifyingReason.GetReason(disqualifyingReasons, patientDisqualifyingReason);
      return `Non-Can: ${disqualifyingReason.name}`;
    }
  }
};

const diabetesNoteTag: INoteTag<{ medicalConclusions: Pick<IMedicalConclusions, "hasDiabetes"> }> = {
  buildTagContent: ({ medicalConclusions: { hasDiabetes } }) => (hasDiabetes ? "Diabetic" : null)
};

const radiationProtectedAreasNoteTag: INoteTag<{
  medicalConclusions: Pick<IMedicalConclusions, "hasRadiationProtectedAreas">;
}> = {
  buildTagContent: ({ medicalConclusions: { hasRadiationProtectedAreas } }) =>
    hasRadiationProtectedAreas ? "Radiation area restriction" : null
};

const pacemakerWithoutDefibrillatorNoteTag: INoteTag<{
  medicalConclusions: Pick<IMedicalConclusions, "hasPacemakerWithoutDefibrillator">;
}> = {
  buildTagContent: ({ medicalConclusions: { hasPacemakerWithoutDefibrillator } }) =>
    hasPacemakerWithoutDefibrillator ? "Pacemaker evaluation at consult" : null
};

const NoteTags = {
  businessUnitTag,
  centerNameNoteTag,
  patientDateOfBirthNoteTag,
  patientDistanceToCenterNoteTag,
  patientDistanceToReservationCenterNoteTag,
  patientHeightWeightBmiTag,
  patientMedicalQualificationPassFailNoteTag,
  patientMedicalQualificationTimestampedNoteTag,
  diabetesNoteTag,
  disqualifyingReasonsNoteTag,
  obxTag,
  pacemakerWithoutDefibrillatorNoteTag,
  radiationProtectedAreasNoteTag,
  serviceSubCategoryName
};

export default NoteTags;
